<template>
  <div
    class="h-full rounded-xl border border-gray-200 bg-white shadow-sm dark:border-white/10 dark:bg-gray-900/60 hover:shadow md:transition-shadow"
    :class="{ 'md:col-span-2': wide }"
    aria-label="External runner card"
    data-testid="external-runner-card"
  >
    <div class="w-full p-5">
      <div class="flex flex-col gap-4">
        <h3
          class="flex flex-wrap items-center gap-2 text-sm font-semibold text-gray-900 break-words dark:text-white"
        >
          <HighlightText :text="runner.name" :query="searchQuery ?? ''" />
          <span
            class="inline-flex items-center rounded-md bg-sky-50 px-2 py-0.5 text-xs font-medium text-sky-700 inset-ring inset-ring-sky-600/20 dark:bg-sky-400/10 dark:text-sky-300 dark:inset-ring-sky-400/30"
            title="A self-hosted server that reports metrics; Manage Runners does not create or control it."
            data-testid="external-badge"
          >
            External
          </span>
        </h3>
        <p
          class="-mt-3 text-xs font-medium text-gray-500 dark:text-gray-400"
          data-testid="external-platform"
        >
          {{ PLATFORM_LABELS[runner.platform] }}
        </p>
        <div v-if="!readOnly" class="flex flex-wrap items-center justify-end gap-2 sm:gap-4">
          <button
            type="button"
            :disabled="planRequired || !installable(runner)"
            :class="buttonClass"
            :title="installTitle"
            data-testid="external-install"
            @click="emit('install')"
          >
            <CommandLineIcon class="size-5" aria-hidden="true" />
            <span class="hidden sm:inline">Install command</span>
          </button>
          <button
            type="button"
            :disabled="planRequired"
            :class="buttonClass"
            :title="planRequired ? 'This needs an active plan.' : 'Edit'"
            aria-label="Edit external runner"
            @click="emit('edit')"
          >
            <PencilSquareIcon class="size-5" aria-hidden="true" />
            <span class="hidden sm:inline">Edit</span>
          </button>
          <button
            type="button"
            :class="buttonClass"
            title="Delete"
            aria-label="Delete external runner"
            data-testid="external-delete"
            @click="emit('delete')"
          >
            <TrashIcon class="size-5" aria-hidden="true" />
            <span class="hidden sm:inline">Delete</span>
          </button>
        </div>
      </div>

      <div
        v-if="runner.reinstallNeeded"
        class="mt-4 flex items-start gap-3 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300"
        role="status"
        data-testid="external-reinstall"
      >
        <ArrowPathIcon class="mt-0.5 size-5 shrink-0" aria-hidden="true" />
        <p class="flex-1">{{ reinstallMessage(runner) }}</p>
      </div>

      <div
        v-if="runner.installedAt && !installable(runner)"
        class="mt-4 flex items-start gap-3 rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm text-gray-700 dark:border-white/10 dark:bg-white/5 dark:text-gray-300"
        role="status"
        data-testid="external-uninstall-hint"
      >
        <p class="flex-1">
          Neither <strong>Show in dashboard</strong> nor <strong>Send to</strong> is selected, so
          the agent stopped. Remove it from the server with <code>{{ UNINSTALL_COMMAND }}</code
          >, or select a destination and run a new install command.
        </p>
      </div>

      <slot name="autoscaling" />

      <div class="my-4 border-t border-gray-200 dark:border-white/10" />

      <div :class="{ 'xl:grid xl:grid-cols-2 xl:items-start xl:gap-x-6': wide }">
        <dl class="grid grid-cols-2 gap-x-6 gap-y-4">
          <div class="space-y-1">
            <dt :class="termClass">Status</dt>
            <dd class="text-sm font-medium">
              <span
                class="inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold"
                :class="STATUS_CLASSES[runner.status]"
                data-testid="external-status"
              >
                <span class="inline-block size-2 rounded-full bg-current" aria-hidden="true" />
                {{ EXTERNAL_STATUS_LABELS[runner.status] }}
              </span>
              <span v-if="lastSeen" class="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                {{ lastSeen }}
              </span>
              <span
                v-if="staleSince"
                class="mt-1 block text-xs text-amber-700 dark:text-amber-400"
                data-testid="external-stale"
              >
                Status as of
                {{
                  new Date(staleSince).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                }}
              </span>
            </dd>
          </div>
          <div class="space-y-1">
            <dt :class="termClass">CPU</dt>
            <dd :class="valueClass" data-testid="external-cpu">{{ cpu ?? '—' }}</dd>
          </div>
          <div class="space-y-1">
            <dt :class="termClass">RAM</dt>
            <dd :class="valueClass">{{ gigabytes(runner.specs?.memoryTotal) ?? '—' }}</dd>
          </div>
          <div class="space-y-1">
            <dt :class="termClass">Disk</dt>
            <dd :class="valueClass">{{ gigabytes(runner.specs?.diskTotal) ?? '—' }}</dd>
          </div>
          <div v-if="runner.labels.length" class="col-span-2 space-y-1">
            <dt :class="termClass">Labels</dt>
            <dd class="flex flex-wrap gap-1.5">
              <span
                v-for="label in runner.labels"
                :key="label"
                class="inline-flex items-center rounded-md bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600 inset-ring inset-ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:inset-ring-gray-400/20"
              >
                <HighlightText :text="label" :query="searchQuery ?? ''" />
              </span>
            </dd>
          </div>
          <div v-if="runner.metricsConnectionId" class="col-span-2 space-y-1">
            <dt :class="termClass">Metrics</dt>
            <dd :class="valueClass">
              {{ runner.metricsConnectionName ?? runner.metricsConnectionId }}
              <span
                v-if="runner.metricsStatus && runner.metricsStatus.status !== 'ok'"
                class="text-xs font-normal text-gray-500 dark:text-gray-400"
              >
                · {{ exportStatusText(runner.metricsStatus.status) }},
                {{ reportedAgo(runner.metricsStatus.reportedAt, now) }}
              </span>
            </dd>
          </div>
          <div v-if="runner.agentVersion" class="space-y-1">
            <dt :class="termClass">Agent</dt>
            <dd :class="valueClass">{{ runner.agentVersion }}</dd>
          </div>
        </dl>

        <div
          v-if="monitoring"
          class="mt-4 border-t border-gray-200 pt-4 dark:border-white/10"
          :class="{ 'xl:mt-0 xl:border-t-0 xl:border-l xl:pt-0 xl:pl-6': wide }"
          data-testid="external-monitoring"
        >
          <RunnerMonitoringPanel v-bind="monitoring" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import {
  ArrowPathIcon,
  CommandLineIcon,
  PencilSquareIcon,
  TrashIcon,
} from '@heroicons/vue/20/solid';
import HighlightText from '@/components/HighlightText.vue';
import RunnerMonitoringPanel from '@/components/RunnerMonitoringPanel.vue';
import type { ExternalRunnerResponse, ExternalRunnerStatus } from '@/api/external-runners';
import type { RunnerMonitoringView } from '@/lib/runner-metrics';
import { exportStatusText, reportedAgo } from '@/lib/monitoring-format';
import { PLATFORM_LABELS } from '@/lib/platforms';
import {
  cpuSummary,
  EXTERNAL_STATUS_LABELS,
  gigabytes,
  installable,
  reinstallMessage,
  UNINSTALL_COMMAND,
} from '@/lib/external-runners';

const props = defineProps<{
  runner: ExternalRunnerResponse;
  monitoring: RunnerMonitoringView | null;
  wide?: boolean;
  readOnly?: boolean;
  planRequired?: boolean;
  searchQuery?: string;
  now: number;
  /** When the shown status was loaded, if it could not be refreshed since. */
  staleSince?: number | null;
}>();

const emit = defineEmits<{
  (e: 'install'): void;
  (e: 'edit'): void;
  (e: 'delete'): void;
}>();

const STATUS_CLASSES: Record<ExternalRunnerStatus, string> = {
  WAITING_FOR_INSTALL: 'bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300',
  ONLINE: 'bg-green-50 text-green-700 dark:bg-green-500/10 dark:text-green-400',
  OFFLINE: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  REVOKED: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
};

const buttonClass =
  'disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:border-white/10 dark:bg-gray-800 dark:text-gray-100 dark:hover:bg-gray-700 cursor-pointer';
const termClass = 'text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400';
const valueClass = 'text-sm font-medium text-gray-900 dark:text-white';

const cpu = computed(() =>
  cpuSummary(props.runner.specs?.cpuLogical, props.runner.specs?.cpuCores)
);

const installTitle = computed(() => {
  if (props.planRequired) return 'This needs an active plan.';
  if (!installable(props.runner)) return 'Enable Monitoring or select a Metrics connection first.';
  return 'Install or update the agent on the server';
});

const lastSeen = computed(() => {
  if (!props.runner.lastSeenAt) return '';
  const minutes = Math.floor((props.now - Date.parse(props.runner.lastSeenAt)) / 60_000);
  if (Number.isNaN(minutes)) return '';
  if (minutes < 1) return 'Last report just now';
  if (minutes < 120) return `Last report ${minutes} min ago`;
  return `Last report ${new Date(props.runner.lastSeenAt).toLocaleString()}`;
});
</script>
